import React, { ReactElement } from 'react';
import { Button, Checkbox, Divider, Input, List, Space } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';
import VirtualList from 'rc-virtual-list';
import Flexbox from '@common/components/Flexbox';
import * as Styled from './EnhancedDropdownLayout.styled';

interface EnhancedDropdownLayoutProps<T> {
  /**
   * Hide right side of Dropdown.
   */
  hideSelected: boolean;
  /**
   * Title for header.
   */
  title: string;
  /**
   * Show  elected elements count.
   */
  selectedText: string;
  /**
   * Array of items;.
   */
  items: T[];
  /**
   * Array of selected items;.
   */
  selectedItems: T[];
  /**
   * Simple for testing id.
   */
  dataTestId: string;
  /**
   * Hide Cancel button.
   */
  hideCancelButton: boolean;
  /**
   * Hide Selection actions buttons.
   */
  hideSelectionActions: boolean;
  /**
   * Hide Search.
   */
  hideSearch: boolean;
  /**
   * Searched text.
   */
  searchText: string;
  /**
   * Callback for click on Cancel.
   */
  handleCancel: () => void;
  /**
   * Callback for click on Apply.
   */
  handleApply: () => void;
  /**
   * Callback for click on SelectAll.
   */
  handleSelectAll: () => void;
  /**
   * Callback for click on ClearAll.
   */
  handleClearAll: () => void;
  /**
   * Callback for toggle select item.
   */
  handleToggleSelect: (item: T) => (event: CheckboxChangeEvent) => void;
  /**
   * Callback for search Input.
   */
  handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Callback for delete selected.
   */
  handleDeleteSelected: (item: T) => () => void;
}

/**
 * Layout for EnhancedDropdown use this with hook useEnhancedDropdown
 * @params EnhancedDropdownLayoutProps<T>
 * @returns Layout with search and selected items.
 */
const EnhancedDropdownLayout = <T extends { text: string; id: number | string }>({
  hideSelected,
  title,
  selectedText,
  items,
  selectedItems,
  dataTestId,
  hideCancelButton,
  handleCancel,
  handleApply,
  hideSearch,
  searchText,
  hideSelectionActions,
  handleSelectAll,
  handleClearAll,
  handleToggleSelect,
  handleDeleteSelected,
  handleSearch,
}: EnhancedDropdownLayoutProps<T>): ReactElement => {
  return (
    <Styled.EnhancedDropdownLayoutCardWrapper
      fullwidth={hideSelected}
      bordered={false}
      title={
        <Flexbox alignItems='center' justifyContent='center'>
          <Styled.EnhancedDropdownLayoutCardHeader>{title}</Styled.EnhancedDropdownLayoutCardHeader>
          {!hideSelected && (
            <>
              <Styled.EnhancedDropdownLayoutDivider type='vertical' />
              <Styled.EnhancedDropdownLayoutCardHeader>{selectedText}</Styled.EnhancedDropdownLayoutCardHeader>
            </>
          )}
        </Flexbox>
      }
      actions={[
        <Flexbox key={dataTestId} justifyContent='flex-end'>
          <Space size={10}>
            {!hideCancelButton && (
              <Button type='link' data-test-id={`${dataTestId}-cancel-button`} onClick={handleCancel} size='small'>
                Cancel
              </Button>
            )}
            <Button
              size='small'
              type='primary'
              data-test-id={`${dataTestId}-apply-button`}
              shape='round'
              onClick={handleApply}>
              Apply
            </Button>
          </Space>
        </Flexbox>,
      ]}>
      <Flexbox justifyContent='center'>
        <Styled.EnhancedDropdownLayoutCardBody>
          {!hideSearch && (
            <Flexbox style={{ padding: '15px 10px 10px 10px' }}>
              <Input
                data-test-id={`${dataTestId}-search`}
                placeholder='Search'
                value={searchText}
                suffix={<Styled.EnhancedDropdownLayoutSearchIcon rev={undefined} />}
                onChange={handleSearch}
                allowClear
              />
            </Flexbox>
          )}
          {!hideSelectionActions && (
            <Flexbox style={{ padding: 10 }}>
              <Button
                data-test-id={`${dataTestId}-select-all-button`}
                type='link'
                size='small'
                onClick={handleSelectAll}>
                Select All
              </Button>
              <Divider type='vertical' style={{ height: 24 }} />
              <Button data-test-id={`${dataTestId}-clear-all-button`} type='link' size='small' onClick={handleClearAll}>
                Clear All
              </Button>
            </Flexbox>
          )}
          {(!hideSelectionActions || !hideSearch) && <Divider style={{ margin: '0 0 15px 0' }} />}
          <List>
            <VirtualList data={items} itemKey={'id'} height={250} itemHeight={32}>
              {(item) => (
                <Styled.EnhancedDropdownLayoutItem key={item.id}>
                  <Checkbox
                    data-test-id={`${dataTestId}-item-${item.id}`}
                    onChange={handleToggleSelect(item)}
                    checked={selectedItems.some((selectedItem) => selectedItem.id === item.id)}>
                    {item.text}
                  </Checkbox>
                </Styled.EnhancedDropdownLayoutItem>
              )}
            </VirtualList>
          </List>
          <Styled.EnhancedDropdownLayoutItem></Styled.EnhancedDropdownLayoutItem>
        </Styled.EnhancedDropdownLayoutCardBody>
        {!hideSelected && (
          <>
            <Styled.EnhancedDropdownLayoutDivider type='vertical' />
            <Styled.EnhancedDropdownLayoutCardBody>
              {selectedItems.length ? (
                <List style={{ padding: '15px 0' }}>
                  <VirtualList data={selectedItems} itemKey={'id'} height={350} itemHeight={32}>
                    {(item) => (
                      <Styled.EnhancedDropdownLayoutItem
                        key={`${item.id}-selected`}
                        actions={[
                          <Styled.EnhancedDropdownLayoutDeleteSelectedButton
                            key={`${item.id}-selected-remove`}
                            data-test-id={`${item.id}-selected-remove`}
                            onClick={handleDeleteSelected(item)}
                            rev={undefined}
                          />,
                        ]}>
                        <Styled.EnhancedDropdownLayoutItemText>{item.text}</Styled.EnhancedDropdownLayoutItemText>
                      </Styled.EnhancedDropdownLayoutItem>
                    )}
                  </VirtualList>
                </List>
              ) : (
                <Styled.EnhancedDropdownLayoutEmptySelected>
                  Nothing selected..
                </Styled.EnhancedDropdownLayoutEmptySelected>
              )}
            </Styled.EnhancedDropdownLayoutCardBody>
          </>
        )}
      </Flexbox>
    </Styled.EnhancedDropdownLayoutCardWrapper>
  );
};

export default EnhancedDropdownLayout;
